/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useConferenceData.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for loading conference data and options.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { buildApiUrl, getApiHeaders } from '../../../config/api';
import { fetchConferenceById, fetchConferenceQuicklist } from '../api/conferencesApi';
import { createDefaultConferenceData } from '../utils/formDefaults';
import type { ConferenceRoom } from '../types';

interface UseConferenceDataOptions {
  conference?: ConferenceRoom | null;
  mode: 'create' | 'edit';
  selectedDomainId?: number;
  tenantSlug?: string;
  userId?: string | number;
}

export function useConferenceData({
  conference,
  mode,
  selectedDomainId,
  tenantSlug,
  userId,
}: UseConferenceDataOptions) {
  const location = useLocation();
  const [formData, setFormData] = useState<ConferenceRoom>(createDefaultConferenceData);
  const [conferenceProfiles, setConferenceProfiles] = useState<any[]>([]);
  const [mohClasses, setMohClasses] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
  const [isLoadingMoh, setIsLoadingMoh] = useState(false);
  const lastLocationKeyRef = useRef<string>('');

  // Load conference profiles
  useEffect(() => {
    const loadConferenceProfiles = async () => {
      try {
        setIsLoadingProfiles(true);
        const response = await fetch(
          buildApiUrl('/api/telephony/conference-profiles?enabled=true'),
          { headers: getApiHeaders(tenantSlug, userId) }
        );

        if (!response.ok) {
          throw new Error('Failed to load conference profiles');
        }

        const result = await response.json();
        setConferenceProfiles(result.data || []);
      } catch (error) {
        console.error('Error loading conference profiles:', error);
        setConferenceProfiles([]);
      } finally {
        setIsLoadingProfiles(false);
      }
    };

    loadConferenceProfiles();
  }, [tenantSlug, userId]);

  // Load MOH classes
  useEffect(() => {
    const loadMohClasses = async () => {
      setIsLoadingMoh(true);
      try {
        const classes = new Set<string>();
        let page = 1;
        const limit = 100;
        let hasMore = true;

        while (hasMore) {
          const response = await fetch(
            buildApiUrl(`/api/telephony/moh-sources?page=${page}&limit=${limit}&enabled=true&sortBy=class_name&sortOrder=asc`),
            {
              method: 'GET',
              headers: getApiHeaders(),
            }
          );

          if (response.ok) {
            const result = await response.json();
            if (result.success && result.data && Array.isArray(result.data)) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              result.data.forEach((moh: any) => {
                if (moh.class_name && typeof moh.class_name === 'string') {
                  classes.add(moh.class_name.trim());
                }
              });

              const pagination = result.pagination;
              if (pagination && pagination.totalPages && page < pagination.totalPages) {
                page++;
              } else {
                hasMore = false;
              }
            } else {
              hasMore = false;
            }
          } else {
            hasMore = false;
          }
        }

        // Always include 'default' option, then add any from database
        const allClasses = new Set<string>(['default']);
        Array.from(classes).forEach(c => allClasses.add(c));
        setMohClasses(Array.from(allClasses).sort());
      } catch (error) {
        console.error('Failed to load MOH classes:', error);
        // Always include 'default' even on error
        setMohClasses(['default']);
      } finally {
        setIsLoadingMoh(false);
      }
    };

    loadMohClasses();
  }, []);

  // Load conference data or system defaults
  useEffect(() => {
    const savedDataKey = 'conferences:saveAndNewData';
    const locationKey = location.key || location.pathname + location.search;

    if (conference && mode === 'edit') {
      const loadedData = {
        ...createDefaultConferenceData(),
        ...conference,
        moderatorPin: conference.moderatorPin || '',
        participantPin: conference.participantPin || '',
        profile_id: conference.profile_id || 1,
      };
      setFormData(loadedData);
      lastLocationKeyRef.current = locationKey;
    } else if (mode === 'create') {
      // Check if we need to reset:
      // 1. Location key changed (React Router navigation)
      // 2. sessionStorage timestamp indicates reset needed
      const resetTimestamp = sessionStorage.getItem(savedDataKey + ':reset');
      const lastResetTimestamp = lastLocationKeyRef.current ?
        sessionStorage.getItem(savedDataKey + ':lastReset') : null;

      const shouldReset = lastLocationKeyRef.current !== locationKey ||
        (resetTimestamp && resetTimestamp !== lastResetTimestamp);

      if (shouldReset) {
        // Clear any saved data to ensure fresh form
        try {
          sessionStorage.removeItem(savedDataKey);
          if (resetTimestamp) {
            sessionStorage.setItem(savedDataKey + ':lastReset', resetTimestamp);
            sessionStorage.removeItem(savedDataKey + ':reset');
          }
        } catch (error) {
          // Ignore errors when clearing
        }

        // Reset form to defaults first to ensure clean state
        setFormData(createDefaultConferenceData());
        lastLocationKeyRef.current = locationKey;

        // Load system defaults for new conferences
        const loadSystemDefaults = async () => {
          try {
            let defaultLanguage = '';
            let defaultTimezone = '';

            if (selectedDomainId) {
              const url = buildApiUrl(`/api/telephony/system-general/${selectedDomainId}`);
              const response = await fetch(url, {
                method: 'GET',
                headers: getApiHeaders(tenantSlug, userId),
              });

              if (response.ok) {
                const result = await response.json();
                if (result.success && result.data) {
                  if (result.data.defaultLanguage) {
                    defaultLanguage = result.data.defaultLanguage;
                  }
                  if (result.data.defaultTimezone) {
                    defaultTimezone = result.data.defaultTimezone;
                  }
                }
              }
            }

            // Apply system defaults to the reset form
            setFormData(prev => {
              // Ensure language has voice appended (LanguageSelector expects 'en-us-emma' format)
              let formattedLanguage = defaultLanguage || prev.language;
              if (formattedLanguage && !formattedLanguage.match(/^[a-z]{2}-[a-z]{2}-[a-z]+$/i)) {
                // Language is just 'en-us' format, append default voice 'emma'
                formattedLanguage = `${formattedLanguage}-emma`;
              }

              return {
                ...prev,
                language: formattedLanguage,
                timezone: defaultTimezone || prev.timezone,
              };
            });
          } catch (error) {
            // Error loading system defaults, use defaults
            setFormData(createDefaultConferenceData());
          }
        };

        loadSystemDefaults();
      }
    }
  }, [conference, mode, selectedDomainId, tenantSlug, userId, location.key, location.pathname, location.search]);

  return {
    formData,
    setFormData,
    conferenceProfiles,
    mohClasses,
    isLoading,
    isLoadingProfiles,
    isLoadingMoh,
  };
}

